import type {
  GuideBlock,
  GuideGroup,
  GuidePage,
  GuideRelated,
  GuideRow,
  GuideVariant,
} from './guideContent';

// Actor graph guides. Copy is verbatim from the Figma file "Network graph"
// (rIKeruJP5BypnUY6xyazSz), flows 1151-2 "Actor group guide" and 1151-3
// "Single actor guide", plus the Filtering page (frames 1293-2288 / 1294-2611,
// 2026-10). The two share Configuration, Filtering and most of the reference
// pages; the single-actor graph holds only actors directly connected to the
// selected actor and its copy says so. `id`s are the "Learn more" / search
// anchors, see guideContent.ts.

type ActorVariant = Exclude<GuideVariant, 'case'>;

const isGroup = (variant: ActorVariant) => variant === 'actor-group';

// "Learn more" entries shared by the use-case pages
const LEARN: Record<string, GuideRelated> = {
  layout: { page: 'configuration', section: 'layout', title: 'Layout' },
  colorScheme: {
    page: 'configuration',
    section: 'color-scheme',
    title: 'Color scheme',
  },
  crossCluster: {
    page: 'configuration',
    section: 'cross-cluster',
    title: 'Cross-cluster links',
  },
  sizeMetric: {
    page: 'configuration',
    section: 'size-metric',
    title: 'Node size metric',
  },
  displayOptions: {
    page: 'configuration',
    section: 'display-options',
    title: 'Display options',
  },
  timeline: {
    page: 'how-to-read',
    section: 'timeline',
    title: 'Activity timeline',
  },
  connections: {
    page: 'how-to-read',
    section: 'connections',
    title: 'Connections',
  },
  communities: {
    page: 'how-to-read',
    section: 'communities',
    title: 'Communities',
  },
  communitiesCalc: {
    page: 'calculations',
    section: 'communities',
    title: 'How communities are calculated',
  },
  categories: {
    page: 'how-to-read',
    section: 'categories',
    title: 'Actor categories',
  },
  results: { page: 'limitations' },
};

const HOW_IT_WORKS_CARDS: GuideBlock = {
  type: 'cards',
  items: [
    {
      page: 'configuration',
      title: 'Configuration',
      text: 'Graph layout, node size, color, and display options.',
    },
    {
      page: 'filtering',
      title: 'Filtering',
      text: 'Filter actors and connections by platform, role, community, and more.',
    },
    {
      page: 'how-to-read',
      title: 'How to read the graph',
      text: 'Nodes, connections, communities, categories, and other graph elements.',
    },
    {
      page: 'calculations',
      title: 'Calculations',
      text: 'How communities, categories, and other graph values are calculated.',
    },
    {
      page: 'limitations',
      title: 'Understanding the results',
      text: 'Context, limitations, and other details that help explain what the graph shows.',
    },
  ],
};

// ---------------------------------------------------------------------------
// Getting started

const GROUP_WELCOME: GuidePage = {
  id: 'welcome',
  title: 'Getting started with Actor Group graph',
  navTitle: 'Getting started',
  blocks: [
    {
      type: 'p',
      text: 'The Actor Group graph shows how actors in an Actor Group are connected to each other and to other actors around them.',
    },
    {
      type: 'p',
      text: 'It is built from Mentions, Reposts, and Links found in publications during the selected time period. The Actor Group is the starting point, but actors outside the group can also appear when they are connected through the data shown in the graph.',
    },
    { type: 'h', text: 'What you can find out' },
    {
      type: 'tiles',
      items: [
        {
          page: 'uc-period',
          title: 'Communication patterns over a specific period',
          text: 'Compare communication across different time periods.',
        },
        {
          page: 'uc-communities',
          title: 'Communities of closely connected actors',
          text: 'See how actors group into communities based on their connections.',
        },
        {
          page: 'uc-handshakes',
          title: 'How closely actors are connected',
          text: 'See how many connection steps (handshakes) separate actors in the graph.',
        },
        {
          page: 'uc-roles',
          title: 'Who broadcasts content and who amplifies it',
          text: 'See which actors broadcast content, amplify others, or actively participate in the network.',
        },
        {
          page: 'uc-cross-platform',
          title: 'Cross-platform connections between actors',
          text: 'See how actors are connected across different platforms.',
        },
        {
          page: 'uc-strong-ties',
          title: 'Strong and frequent relationships',
          text: 'See which connections have the most interactions.',
        },
      ],
    },
    { type: 'h', text: 'How the graph works' },
    HOW_IT_WORKS_CARDS,
  ],
};

const SINGLE_WELCOME: GuidePage = {
  id: 'welcome',
  title: 'Getting started with the Single Actor Graph',
  navTitle: 'Getting started',
  blocks: [
    {
      type: 'p',
      text: 'The Single Actor Graph shows the actors directly connected to a selected actor during the chosen time period.',
    },
    {
      type: 'p',
      text: 'It is built from Mentions, Reposts, and Links found in publications. Unlike the Actor Group Graph, it shows only direct connections to the selected actor, giving you a focused view of their immediate network.',
    },
    { type: 'h', text: 'What you can find out' },
    {
      type: 'tiles',
      items: [
        {
          page: 'uc-closest',
          title: 'The actor’s closest network',
          text: 'See which actors are directly connected to the selected actor.',
        },
        {
          page: 'uc-cross-platform',
          title: 'Cross-platform connections around the actor',
          text: 'See whether the selected actor is directly connected to actors on other platforms.',
        },
        {
          page: 'uc-strong-ties',
          title: 'Strongest direct relationships',
          text: 'See which actors interact with the selected actor most frequently.',
        },
        {
          page: 'uc-mutual',
          title: 'Mutual relationships',
          text: 'See which actors have connections with the selected actor in both directions.',
        },
        {
          page: 'uc-hra',
          title: 'High Risk Actors in the immediate network',
          text: 'See whether any actors directly connected to the selected actor are marked High Risk in the platform.',
        },
      ],
    },
    { type: 'h', text: 'How the graph works' },
    HOW_IT_WORKS_CARDS,
  ],
};

// ---------------------------------------------------------------------------
// Use cases: Actor Group graph

const GROUP_USE_CASES: GuidePage[] = [
  {
    id: 'uc-period',
    title: 'Communication patterns over a specific period',
    navTitle: 'Communication patterns',
    blocks: [
      {
        type: 'p',
        text: 'Choose the graph time range based on what you want to investigate. A longer period, such as six months, can help reveal relationships that remain active over time. A shorter period can help you focus on communication around a specific event, such as an election, protest, or information campaign.',
      },
      {
        type: 'p',
        text: 'This helps you distinguish long-term relationships from connections that appear only during a particular period.',
      },
      { type: 'h', text: 'Set up the graph' },
      {
        type: 'steps',
        items: [
          'Turn on **Activity timeline** in **Configuration**.',
          'Choose the time range you want to investigate.',
          'Use a longer period to look for relationships that remain active over time.',
          'Use a shorter period to focus on communication around a specific event or campaign.',
          'Use the timeline to focus on a shorter date range or play through the activity over time.',
        ],
      },
      { type: 'related', items: [LEARN.timeline, LEARN.results] },
    ],
  },
  {
    id: 'uc-communities',
    title: 'Communities of closely connected actors',
    navTitle: 'Communities of actors',
    blocks: [
      {
        type: 'p',
        text: 'The Actor Group graph groups actors into communities based on their connections. Some communities may have little interaction with each other, while others may have many connections between their members. This helps you see which actors tend to communicate within the same network and which groups may be worth looking at more closely.',
      },
      { type: 'h', text: 'Set up the graph' },
      {
        type: 'steps',
        items: [
          {
            text: 'Set **Layout** to **Separated clusters**.',
            why: 'This places each community in its own space, making the groups easier to distinguish.',
          },
          {
            text: 'Set **Color scheme** to **By community**.',
            why: 'Actors in the same community share the same color.',
          },
          {
            text: 'Set **Cross-cluster links** to **Dimmed**.',
            why: 'Connections between communities stay visible but appear faded, so the communities are easier to see separately.',
          },
          {
            text: 'Set **Node size metric** to **Degree**.',
            why: 'Actors with more connections appear larger, which helps you spot the most connected actors in each community.',
          },
          {
            text: 'Optional: turn on **Thickness by interactions**.',
            why: 'Connections with more interactions appear thicker, which helps you see where more communication happens.',
          },
        ],
      },
      {
        type: 'related',
        items: [
          LEARN.layout,
          LEARN.colorScheme,
          LEARN.crossCluster,
          LEARN.sizeMetric,
          LEARN.displayOptions,
          LEARN.communities,
          LEARN.communitiesCalc,
          LEARN.results,
        ],
      },
    ],
  },
  {
    id: 'uc-handshakes',
    title: 'How closely actors are connected',
    blocks: [
      {
        type: 'p',
        text: 'See how many connection steps separate actors in the graph. A handshake is not a graph setting. It simply describes how many connections you need to follow to reach another actor.',
      },
      {
        type: 'p',
        text: 'A first-handshake actor has a direct connection to the selected actor. Second- and third-handshake actors are connected through other actors.',
      },
      {
        type: 'p',
        text: 'Together with the community layout, Handshakes help you see which actors are closest to the selected actor and which belong to the wider surrounding network. Actors several handshakes away may be less relevant to a particular investigation, while direct connections can provide a stronger starting point.',
      },
      { type: 'h', text: 'Explore the graph' },
      {
        type: 'steps',
        items: [
          'Select the actor you want to investigate.',
          'Review the actors directly connected to it. These are the first-handshake actors.',
          'Select one of those actors to see its connections.',
          'Continue selecting connected actors to explore second-, third-, and further-handshake actors.',
          'Optional: set **Layout** to **Separated clusters** to see which communities these actors belong to.',
        ],
      },
      {
        type: 'related',
        items: [
          LEARN.layout,
          LEARN.connections,
          LEARN.communities,
          LEARN.results,
        ],
      },
    ],
  },
  {
    id: 'uc-roles',
    title: 'Who broadcasts content and who amplifies it',
    navTitle: 'Broadcasters and amplifiers',
    blocks: [
      {
        type: 'p',
        text: 'Use inbound and outbound connections to understand the role different actors play in the network. Actors that receive many connections and send relatively few may act as **Broadcasters**, while actors that frequently repost or mention others may act as **Amplifiers**. Actors with a more balanced pattern of activity may appear as **Active Participants**.',
      },
      {
        type: 'p',
        text: 'Mutual connections provide additional context by showing actors that interact with each other in both directions.',
      },
      { type: 'h', text: 'Explore the graph' },
      {
        type: 'steps',
        items: [
          'Open the **Actors panel**.',
          {
            text: 'Under **Categories**, select **Broadcaster**, **Amplifier**, or **Active Participant**.',
            why: 'The actors in that category are highlighted on the graph and listed in the panel.',
          },
          'Switch between the categories to compare how different actors behave in the network.',
          {
            text: 'Turn on **Direction arrows** in **Configuration**.',
            why: 'This makes it easier to see which connections are inbound and which are outbound.',
          },
          'Select an actor to open **Actor details** and review its inbound, outbound, and mutual connections.',
        ],
      },
      {
        type: 'related',
        items: [
          LEARN.categories,
          LEARN.displayOptions,
          LEARN.connections,
          LEARN.results,
        ],
      },
    ],
  },
  {
    id: 'uc-cross-platform',
    title: 'Cross-platform connections between actors',
    navTitle: 'Cross-platform connections',
    blocks: [
      {
        type: 'p',
        text: 'See how actors are connected across different platforms. For example, a website may repeatedly mention or link to Telegram channels. The Actor Group graph can connect these actors even though they are on different platforms.',
      },
      {
        type: 'p',
        text: 'This helps you understand how communication or content distribution extends beyond a single platform.',
      },
      { type: 'h', text: 'Explore the graph' },
      {
        type: 'steps',
        items: [
          {
            text: 'Set **Color scheme** to **By platform**.',
            why: 'Actors from the same platform share the same color.',
          },
          {
            text: 'Turn on **Direction arrows**.',
            why: 'This helps you see which actor created each cross-platform connection.',
          },
          'Optional: set **Color scheme** to **By interaction type** to see whether a connection comes from a Mention, Repost, or Link.',
          'Select an actor to open **Actor details** and review its connections to actors on other platforms.',
          'Follow those connections to explore how the network extends across platforms.',
        ],
      },
      {
        type: 'related',
        items: [
          LEARN.colorScheme,
          LEARN.displayOptions,
          LEARN.connections,
          LEARN.results,
        ],
      },
    ],
  },
  {
    id: 'uc-strong-ties',
    title: 'Strong and frequent relationships',
    navTitle: 'Strongest interactions',
    blocks: [
      {
        type: 'p',
        text: 'Use **Thickness by interactions** to find connections with the highest number of interactions. The more frequently two actors interact, the thicker the connection between them appears. This helps you quickly spot relationships that may be worth investigating more closely.',
      },
      {
        type: 'p',
        text: 'A particularly strong connection can provide a useful starting point for further investigation, especially when it involves an actor you already consider relevant.',
      },
      { type: 'h', text: 'Explore the graph' },
      {
        type: 'steps',
        items: [
          {
            text: 'Turn on **Thickness by interactions** in **Configuration**.',
            why: 'Connections with more interactions appear thicker.',
          },
          'Look for the thickest connections on the graph.',
          'Select one of the actors in a strong connection to open **Actor details**.',
          'Review the actor’s inbound, outbound, and mutual connections.',
          'Follow the strongest connections to identify relationships that may need closer investigation.',
          'Optional: use **Color scheme** to add context, for example **By actor group**, **By platform**, or **By interaction type**.',
        ],
      },
      { type: 'related', items: [LEARN.displayOptions, LEARN.results] },
    ],
  },
];

// ---------------------------------------------------------------------------
// Use cases: Single Actor graph

const SINGLE_USE_CASES: GuidePage[] = [
  {
    id: 'uc-closest',
    title: 'The actor’s closest network',
    navTitle: 'The closest network',
    blocks: [
      {
        type: 'p',
        text: 'See which actors are directly connected to the selected actor. The Single Actor Graph shows only first-handshake connections. Every actor in the graph is directly connected to the selected actor through a Mention, Repost, or Link.',
      },
      {
        type: 'p',
        text: 'This gives you a focused view of the actor’s immediate network without expanding into second- or third-handshake connections.',
      },
      { type: 'h', text: 'Explore the graph' },
      {
        type: 'steps',
        items: [
          'Start with the selected actor in the graph.',
          'Review the actors directly connected to it.',
          'Select an actor to open **Actor details**.',
          'Review how that actor is connected to the selected actor.',
          'Turn on **Direction arrows** if you want to see the direction of the connection.',
        ],
      },
      {
        type: 'related',
        items: [LEARN.displayOptions, LEARN.connections, LEARN.results],
      },
    ],
  },
  {
    id: 'uc-cross-platform',
    title: 'Cross-platform connections around the actor',
    navTitle: 'Cross-platform connections',
    blocks: [
      {
        type: 'p',
        text: 'See whether the selected actor is directly connected to actors on other platforms. For example, a website may mention or link to a Telegram channel. The Single Actor Graph can show this connection even though the actors are on different platforms.',
      },
      {
        type: 'p',
        text: 'This helps you understand whether the actor’s immediate network extends beyond a single platform.',
      },
      { type: 'h', text: 'Explore the graph' },
      {
        type: 'steps',
        items: [
          'Set **Color scheme** to **By platform**.',
          'Look for actors connected to the selected actor that use a different platform.',
          'Turn on **Direction arrows** to see which actor created the connection.',
          'Select an actor to open **Actor details** and review the connection.',
          'Optional: set **Color scheme** to **By interaction type** to see whether the connection comes from a Mention, Repost, or Link.',
        ],
      },
      {
        type: 'related',
        items: [LEARN.displayOptions, LEARN.connections, LEARN.results],
      },
    ],
  },
  {
    id: 'uc-strong-ties',
    title: 'Strongest direct relationships',
    blocks: [
      {
        type: 'p',
        text: 'See which actors interact with the selected actor most frequently. Turn on **Thickness by interactions** to make connections with more interactions appear thicker.',
      },
      {
        type: 'p',
        text: 'Because every actor in the graph is directly connected to the selected actor, the thickest connections help you quickly find the most frequent direct relationships.',
      },
      { type: 'h', text: 'Explore the graph' },
      {
        type: 'steps',
        items: [
          'Turn on **Thickness by interactions** in **Configuration**.',
          'Look for the thickest connections to the selected actor.',
          'Select one of the connected actors to open **Actor details**.',
          'Review the interaction count and connection direction.',
          'Compare several strong connections to see which actors interact with the selected actor most often.',
        ],
      },
      { type: 'related', items: [LEARN.displayOptions, LEARN.results] },
    ],
  },
  {
    id: 'uc-mutual',
    title: 'Mutual relationships',
    blocks: [
      {
        type: 'p',
        text: 'See which actors have connections with the selected actor in both directions. A mutual connection means the selected actor connects to another actor and that actor also connects back.',
      },
      {
        type: 'p',
        text: 'Frequent mutual interaction can provide useful context about a relationship, but it does not by itself prove coordination.',
      },
      { type: 'h', text: 'Explore the graph' },
      {
        type: 'steps',
        items: [
          'Turn on **Direction arrows** in **Configuration**.',
          'Select an actor connected to the selected actor.',
          'Open **Actor details**.',
          'Review whether the connection is **Inbound**, **Outbound**, or **Mutual**.',
          'Look at the interaction count to understand how often the actors interact.',
          'Compare mutual connections with other direct relationships around the selected actor.',
        ],
      },
      {
        type: 'related',
        items: [LEARN.displayOptions, LEARN.connections, LEARN.results],
      },
    ],
  },
  {
    id: 'uc-hra',
    title: 'High Risk Actors in the immediate network',
    navTitle: 'High-risk actors nearby',
    blocks: [
      {
        type: 'p',
        text: 'See whether any actors directly connected to the selected actor are marked High Risk in the platform. Because the Single Actor Graph shows only first-handshake connections, every High Risk Actor shown in the graph has a direct connection to the selected actor.',
      },
      {
        type: 'p',
        text: 'This can provide a useful starting point for examining the actor’s surrounding network more closely.',
      },
      { type: 'h', text: 'Explore the graph' },
      {
        type: 'steps',
        items: [
          'Open the **Actors panel**.',
          'Under **Categories**, select **High Risk Actors**.',
          'High Risk Actors are highlighted on the graph and listed in the panel.',
          'Select an actor to open **Actor details**.',
          'Review whether the connection with the selected actor is **Inbound**, **Outbound**, or **Mutual**.',
          'Turn on **Direction arrows** or **Thickness by interactions** if you want more context about the connection.',
        ],
      },
      { type: 'related', items: [LEARN.categories, LEARN.results] },
    ],
  },
];

// ---------------------------------------------------------------------------
// Configuration (shared; the colour schemes differ)

const colorSchemeRows = (variant: ActorVariant): GuideRow[] => [
  {
    key: 'By community',
    text: 'Actors in the same community share the same color. Connections use the color of their community.',
  },
  {
    key: 'By interaction type',
    text: 'Connections use different colors for Mentions, Reposts, and Links. If two actors are connected in more than one way, the connection uses the color of the most common type.',
  },
  // the actor-group schemes need the group boundary — a single-actor graph has none
  ...(isGroup(variant)
    ? [
        {
          key: 'By actor group',
          text: 'Actors in the Actor Group share one color. Actors that appear in the graph but are not part of the Actor Group use another color.',
        },
        {
          key: 'By connection (internal / external)',
          text: 'Connections going into the Actor Group use one color, and connections going out use another. Connections that do not cross the Actor Group boundary stay neutral.',
        },
      ]
    : []),
  {
    key: 'By platform',
    text: 'Actors from the same platform share the same color.',
  },
  { key: 'Monochrome', text: 'Shows the whole graph in one color.' },
];

const configurationPage = (variant: ActorVariant): GuidePage => ({
  id: 'configuration',
  title: 'Configuration',
  blocks: [
    {
      type: 'p',
      text: 'Use Configuration to change how the graph looks. These settings do not change the data or calculations behind the graph.',
    },
    { type: 'h', text: 'Node size metric', id: 'size-metric' },
    {
      type: 'p',
      text: 'Each node represents an actor. This setting controls how large each node appears on the graph.',
    },
    {
      type: 'rows',
      rows: [
        {
          id: 'degree',
          key: 'Degree',
          text: 'Shows node size based on the number of connections.',
          rule: 'More connections = a larger node.',
          why: 'For example, an actor with 20 connections appears larger than an actor with 5.',
        },
        {
          id: 'pagerank',
          key: 'PageRank',
          text: 'Shows node size based on both the number of connections and who those connections come from.',
          rule: 'More connections from actors that also have many connections = a larger node.',
          why: 'A connection from a highly connected actor increases the node size more than a connection from an actor with only a few connections. This means an actor with fewer connections can still appear larger if those connections come from highly connected actors.',
        },
      ],
    },
    { type: 'h', text: 'Layout', id: 'layout' },
    {
      type: 'p',
      text: 'Changes where actors and communities appear on the graph. The connections stay the same. Only their position on the screen changes.',
    },
    {
      type: 'rows',
      rows: [
        {
          id: 'layout-organic',
          key: 'Organic bridges',
          text: 'Keeps connected communities closer together.',
          rule: 'More connections between two communities = they appear closer together.',
          why: 'Actors with connections to more than one community tend to appear between those communities.',
        },
        {
          id: 'layout-packed',
          key: 'Separated clusters',
          text: 'Places each community in its own separate group.',
          rule: 'Each community gets its own space on the graph.',
          why: 'This makes it easier to see the communities separately and compare their size.',
        },
      ],
    },
    {
      type: 'note',
      text: 'Distance on the screen does not show connection strength.',
    },
    { type: 'h', text: 'Color scheme', id: 'color-scheme' },
    {
      type: 'p',
      text: 'Changes what the colors on the graph represent. The legend in the bottom-left corner shows what each color means.',
    },
    { type: 'rows', rows: colorSchemeRows(variant) },
    { type: 'h', text: 'Cross-cluster links', id: 'cross-cluster' },
    {
      type: 'p',
      text: 'Choose how connections between different communities are shown. Connections inside the same community stay unchanged.',
    },
    {
      type: 'rows',
      rows: [
        {
          key: 'Dimmed',
          text: 'Connections between different communities appear faded. You can still see them, but they are less visible.',
        },
        {
          key: 'Hidden',
          text: 'Connections between different communities are hidden. This leaves only the connections inside each community visible.',
        },
        {
          key: 'Full',
          text: 'Connections between different communities are fully visible. They are shown the same way as connections inside a community.',
        },
      ],
    },
    { type: 'h', text: 'Display options', id: 'display-options' },
    {
      type: 'p',
      text: 'Use Display options to choose which visual elements are shown.',
    },
    {
      type: 'rows',
      rows: [
        {
          id: 'direction-arrows',
          key: 'Direction arrows',
          text: 'Adds arrows to connections so you can see their direction. The arrow points from the actor that mentioned, reposted, or linked to the actor on the other end.',
        },
        {
          id: 'thickness',
          key: 'Thickness by interactions',
          text: 'Changes connection thickness based on how many interactions happened between two actors.',
          rule: 'More interactions = a thicker connection.',
          why: 'When this option is off, all connections stay thin. Connections of the selected actor still show their relative thickness.',
        },
        {
          id: 'highlight-bridges',
          key: 'Highlight critical bridges',
          text: 'Adds a ring around actors that connect to multiple communities. Critical bridges are actors that connect to multiple communities. See [[calculations#critical-bridges|Critical bridges]] for more details.',
        },
        {
          id: 'timeline',
          key: 'Activity timeline',
          text: 'Shows the Activity timeline below the graph. The timeline shows interaction activity over time, with a breakdown by interaction type. See [[how-to-read#timeline|Activity timeline]] for more details.',
        },
      ],
    },
  ],
});

// ---------------------------------------------------------------------------
// Filtering (Figma 1293-2288 group / 1294-2611 single; the Actor group row
// only exists on actor-group graphs)

const filteringPage = (variant: ActorVariant): GuidePage => ({
  id: 'filtering',
  title: 'Filtering',
  blocks: [
    {
      type: 'p',
      text: 'Use **Filters** to focus on the actors and connections you want to look at. Filters change only what is shown on the graph. They do not change the data or calculations behind the graph.',
    },
    { type: 'h', text: 'How filters work', id: 'how-filters-work' },
    {
      type: 'p',
      text: 'Filters are grouped into two tabs: **Actors** and **Connections**.',
    },
    {
      type: 'list',
      items: [
        'Within one filter, the graph shows actors that match *any* of the selected values. For example, selecting **Twitter** and **Telegram** shows actors from either platform.',
        'Across different filters, the graph shows only actors that match *all* of them. For example, selecting **Twitter** in **Platforms** and **Amplifier** in **Actor roles** shows only Amplifiers on Twitter.',
        'If nothing is selected in a filter, that filter does not limit the graph.',
      ],
    },
    { type: 'h', text: 'Dim instead of hide', id: 'dim-instead-of-hide' },
    {
      type: 'p',
      text: 'By default, actors that do not match the filters are hidden. Turn on **Dim instead of hide** to keep them visible but dimmed.',
    },
    {
      type: 'note',
      text: 'This helps you see where the matching actors sit in the whole graph.',
    },
    { type: 'h', text: 'Actor filters', id: 'actor-filters' },
    {
      type: 'p',
      text: 'Use the **Actors** tab to choose which actors appear on the graph.',
    },
    {
      type: 'rows',
      rows: [
        {
          id: 'platforms',
          key: 'Platforms',
          text: 'Shows actors from the selected platforms.',
        },
        {
          id: 'actor-roles',
          key: 'Actor roles',
          text: 'Shows actors with the selected role: Broadcaster, Amplifier, or Active Participant. See [[how-to-read#categories|Actor categories]] for what each role means.',
        },
        ...(isGroup(variant)
          ? [
              {
                id: 'actor-group',
                key: 'Actor group',
                text: 'Shows actors that are in the Actor Group (**In actor group**), actors outside it that appear through their connections (**Not in actor group**), or both.',
              },
            ]
          : []),
        {
          id: 'high-risk-accounts',
          key: 'High risk accounts',
          text: 'Shows actors marked as High Risk in the platform, by High Risk type.',
          why: 'For example, **Information manipulation**.',
        },
        {
          id: 'communities',
          key: 'Communities',
          text: 'Shows actors from the selected communities.',
        },
        {
          id: 'origin-country',
          key: 'Origin country',
          text: 'Shows actors by the country they are from.',
        },
        {
          id: 'audience',
          key: 'Audience',
          text: 'Shows actors by audience size.',
          why: 'Audience is grouped into ranges: **< 1K**, **1K – 10K**, **10K – 100K**, **100K – 1M**, and **≥ 1M**.',
        },
      ],
    },
    {
      type: 'note',
      text: 'Long lists show the most common values first. Select **Show more** to see the rest.',
    },
    { type: 'h', text: 'Connection filters', id: 'connection-filters' },
    {
      type: 'p',
      text: 'Use the **Connections** tab to choose which connections appear on the graph.',
    },
    {
      type: 'rows',
      rows: [
        {
          id: 'interaction-type',
          key: 'Interaction type',
          text: 'Shows connections of the selected types: **Mentions**, **Reposts**, or **Links**.',
        },
        {
          id: 'mutual-only',
          key: 'Mutual connections only',
          text: 'Shows only actors that have mutual connections, where two actors interact with each other in both directions.',
        },
        {
          id: 'min-connections',
          key: 'Minimum connections',
          text: 'Shows only actors with at least the selected number of connections. Inbound and outbound connections are counted together.',
          why: 'Type a number or choose one of the suggested values. The panel shows how many actors match.',
        },
      ],
    },
    { type: 'h', text: 'Active filters', id: 'active-filters' },
    {
      type: 'p',
      text: 'Active filters appear above the graph. Each filter appears as one chip. If several values are selected, the chip shows the first value and how many more are selected, for example **Platforms Twitter +2**.',
    },
    {
      type: 'list',
      items: [
        'Select × on a chip to remove that filter.',
        'Select **Clear all filters** to remove all filters at once.',
      ],
    },
    { type: 'h', text: 'Filters and calculations', id: 'filters-calculations' },
    {
      type: 'p',
      text: 'Communities, actor roles, Degree, and PageRank are calculated for the whole graph. Filtering does not recalculate them. An actor keeps the same community and role when other actors are filtered out.',
    },
  ],
});

// ---------------------------------------------------------------------------
// How to read the graph

const howToReadPage = (variant: ActorVariant): GuidePage => {
  const graphName = isGroup(variant)
    ? 'Actor Group graph'
    : 'Single Actor Graph';
  return {
    id: 'how-to-read',
    title: 'How to read the graph',
    blocks: [
      {
        type: 'p',
        text: 'Learn what the graph shows and where to find details about actors, communities, and activity over time.',
      },
      ...(isGroup(variant)
        ? []
        : ([
            { type: 'h', text: 'Direct connections only', id: 'direct-only' },
            {
              type: 'p',
              text: 'The Single Actor Graph shows only actors directly connected to the selected actor. Every actor in the graph is one connection step away. The graph does not expand to second- or third-handshake actors.',
            },
          ] as GuideBlock[])),
      { type: 'h', text: 'Nodes and connections', id: 'nodes-connections' },
      {
        type: 'p',
        text: 'Each node represents an actor. A connection between two nodes means that one actor mentioned, reposted, or linked to the other. The connection goes from the actor that performed the action to the actor on the other end.',
      },
      {
        type: 'p',
        text: 'If two actors have connections in both directions, they have a **mutual connection**.',
      },
      {
        type: 'p',
        text: 'Node size and color depend on the settings you choose in [[configuration|Configuration]].',
      },
      { type: 'h', text: 'Interaction types', id: 'interaction-types' },
      {
        type: 'rows',
        rows: [
          {
            key: 'Mention',
            text: 'One actor mentions another actor in a publication. The connection goes from the actor that made the mention to the actor that was mentioned.',
          },
          {
            key: 'Repost',
            text: 'One actor shares content originally posted by another actor. The connection goes from the actor that reposted to the actor whose content was reposted.',
          },
          {
            key: 'Link',
            text: 'One actor publishes a link that points to another actor, their page, channel, or content. The link can be visible as a URL or embedded behind text.',
          },
        ],
      },
      {
        type: 'note',
        text: 'The same pair of actors can be connected through more than one interaction type.',
      },
      {
        type: 'h',
        text: 'Inbound, outbound, and mutual connections',
        id: 'connections',
      },
      ...(isGroup(variant)
        ? ([
            {
              type: 'list',
              items: [
                '**Inbound** shows how many actors have connections pointing to the selected actor.',
                '**Outbound** shows how many actors the selected actor has connections pointing to.',
                '**Mutual** shows how many actors have connections with the selected actor in both directions.',
              ],
            },
          ] as GuideBlock[])
        : ([
            {
              type: 'p',
              text: 'In the Single Actor Graph, connection direction is always described relative to the selected actor.',
            },
            {
              type: 'list',
              items: [
                '**Inbound** means the connection points to the selected actor.',
                '**Outbound** means the connection goes from the selected actor to another actor.',
                '**Mutual** means connections exist in both directions between the selected actor and another actor.',
              ],
            },
          ] as GuideBlock[])),
      ...(isGroup(variant)
        ? ([
            { type: 'h', text: 'Actor Group membership', id: 'membership' },
            {
              type: 'p',
              text: 'The graph can include actors that were not part of the Actor Group used to build it.',
            },
            {
              type: 'rows',
              rows: [
                {
                  id: 'in-actor-group',
                  key: 'In actor group',
                  text: 'The actor belongs to the Actor Group used to build the graph.',
                },
                {
                  id: 'not-in-actor-group',
                  key: 'Not in actor group',
                  text: 'The actor is not part of the Actor Group, but appears because of connections shown in the graph.',
                },
              ],
            },
            {
              type: 'note',
              text: 'Use **Color scheme** → **By actor group** to see the difference on the graph.',
            },
          ] as GuideBlock[])
        : []),
      { type: 'h', text: 'Communities', id: 'communities' },
      {
        type: 'p',
        text: isGroup(variant)
          ? 'A community is a group of actors that interact with each other more often than they interact with actors outside the group. Communities help you see which groups of actors are more closely connected. See [[calculations#communities|How communities are calculated]] for more details.'
          : 'A community is a group of actors that interact with each other more often than they interact with actors outside the group. Communities are built within the current Single Actor Graph, using only the actors that appear in it. Communities help you see which groups of actors are more closely connected. See [[calculations#communities|How communities are calculated]] for more details.',
      },
      { type: 'h', text: 'Actor categories', id: 'categories' },
      {
        type: 'p',
        text: `These categories describe the role or status of an actor in the ${graphName}.`,
      },
      {
        type: 'rows',
        rows: [
          {
            id: 'broadcaster',
            key: 'Broadcaster',
            text: 'Receives many connections and sends relatively few.',
          },
          {
            id: 'amplifier',
            key: 'Amplifier',
            text: 'Frequently mentions or reposts other actors and helps spread their content.',
          },
          {
            id: 'active-participant',
            key: 'Active Participant',
            text: 'Interacts frequently across the graph without one dominant pattern.',
          },
          {
            id: 'high-risk',
            key: 'High Risk Actor',
            text: 'Marked as High Risk in the platform. The graph shows this existing status; it does not calculate it.',
          },
          {
            id: 'critical-bridge',
            key: 'Critical bridge',
            text: 'Connects to multiple communities. See [[calculations#critical-bridges|Critical bridges]] for more details.',
          },
          {
            id: 'high-interaction',
            key: 'High-Interaction Actor',
            text: 'Actors with a significantly higher number of interactions than other actors in this graph.',
          },
        ],
      },
      {
        type: 'p',
        text: 'See [[calculations#actor-roles|How actor roles are calculated]] for more details.',
      },
      { type: 'h', text: 'Actors panel', id: 'actors-panel' },
      {
        type: 'p',
        text: 'Use the Actors panel to browse actors by category or community.',
      },
      {
        type: 'rows',
        rows: [
          {
            key: 'Categories',
            text: `Groups actors by their category in the ${graphName}. Select a category to highlight its actors on the graph and open the list of actors in that category.`,
          },
          {
            key: 'Communities',
            text: 'Groups actors by community. Select a community to highlight its actors on the graph and open the list of actors in that community.',
          },
          {
            key: 'Create an Actor Group',
            text: 'You can create an **Actor Group** from a category or community. The Actor Group is saved in the platform and can be used outside the graph.',
          },
        ],
      },
      { type: 'h', text: 'Actor details', id: 'actor-details' },
      {
        type: 'p',
        text: 'Open **Actor details** by selecting an actor on the graph or from the Actors panel. Actor details shows the actor’s categories, community, connections, and interaction counts.',
      },
      ...(isGroup(variant)
        ? ([
            { type: 'h', text: 'The community panel', id: 'community-panel' },
            {
              type: 'p',
              text: 'Opens when you click a community. It describes the community as a whole.',
            },
            {
              type: 'rows',
              rows: [
                {
                  id: 'density',
                  key: 'Density',
                  text: 'How closely connected the community is internally.',
                },
                {
                  key: 'Actors',
                  text: 'The full list of the community’s actors.',
                },
              ],
            },
            {
              type: 'note',
              text: 'The share of **High Risk** actors is shown on the community row in the **Actors** panel, not in this panel.',
            },
          ] as GuideBlock[])
        : []),
      { type: 'h', text: 'Activity timeline', id: 'timeline' },
      {
        type: 'p',
        text: 'Shows how interaction activity changes over time. The timeline breaks activity down by Links, Reposts, and Mentions.',
      },
      {
        type: 'p',
        text: 'Turn on **Activity timeline** in **Configuration** to show it below the graph. Select a date range to focus on a specific period. Press **Play** to watch actors and connections appear over time. You can also change the playback speed.',
      },
    ],
  };
};

// ---------------------------------------------------------------------------
// Calculations

const CRITICAL_BRIDGES_ROW: GuideRow = {
  id: 'critical-bridges',
  key: 'Critical bridges',
  text: 'Critical bridges are calculated from direct connections between actors. Connection direction is ignored. For each actor, the calculation counts how many other communities their direct connections reach. Multiple connections to the same community count once. An actor must connect to at least two other communities to qualify. Qualifying actors are ranked by how many communities they connect to. If actors have the same count, betweenness is used first, then the number of connections. Up to 10 actors are marked as critical bridges.',
};

const ACTOR_ROLES_ROW: GuideRow = {
  id: 'actor-roles',
  key: 'Actor roles',
  text: 'Broadcaster, Amplifier, and Active Participant are calculated from each actor’s connection and ranking patterns across the current graph. The roles compare an actor with the other actors in the same graph, so the same actor can receive a different role in a different graph.',
};

const INTERACTION_COUNTS_ROW: GuideRow = {
  id: 'interaction-counts',
  key: 'Interaction counts',
  text: '**Mentions**, **Reposts**, and **Links** count how many interactions of each type are present across an actor’s connections. Each interaction adds to the count of its type, even when several interactions happen between the same two actors.',
};

const PAGERANK_ROW: GuideRow = {
  id: 'pagerank',
  key: 'PageRank',
  text: 'PageRank considers both the number of connections to an actor and the PageRank of the actors those connections come from. A connection from an actor with a higher PageRank has more effect than a connection from an actor with a lower PageRank. The number of interactions in a connection is also used as its weight. PageRank is relative to the actors in the current graph. Use it to compare actors with each other, not against a fixed scale.',
};

const GROUP_CALCULATIONS: GuidePage = {
  id: 'calculations',
  title: 'Calculations',
  blocks: [
    {
      type: 'p',
      text: 'This section explains how the main values in the Actor Group graph are calculated.',
    },
    {
      type: 'rows',
      rows: [
        {
          id: 'communities',
          key: 'Communities',
          text: 'Communities are found using the **Louvain method**. The calculation looks for groups where actors have stronger interaction connections with each other than with actors outside the group. Connection direction is ignored. The number of interactions between actors is used as the connection weight.',
        },
        {
          id: 'handshakes',
          key: 'Handshakes',
          text: 'The number of connections you cross to reach an actor from the one the graph was built for. Direction is ignored.',
        },
        {
          id: 'degree',
          key: 'Degree',
          text: 'Degree counts how many unique actors are connected to an actor. Inbound and outbound connections are counted together. If the same two actors interact many times, they still count as one connection for Degree.',
        },
        PAGERANK_ROW,
        {
          id: 'connections',
          key: 'Connections',
          text: 'Connections are counted by unique actors, not by the total number of interactions.',
          why: [
            '**Inbound** counts how many actors have a connection pointing to this actor.',
            '**Outbound** counts how many actors this actor points to.',
            '**Mutual** counts how many actors have connections with this actor in both directions.',
            'Multiple interactions between the same two actors do not create additional connections.',
          ],
        },
        {
          id: 'mutual',
          key: 'Mutual connections',
          text: 'The number of actors that have a connection with this one in both directions.',
        },
        INTERACTION_COUNTS_ROW,
        {
          id: 'density',
          key: 'Density',
          text: 'The number of connections that exist, divided by the number there would be if every actor were connected to every other actor.',
        },
        CRITICAL_BRIDGES_ROW,
        ACTOR_ROLES_ROW,
      ],
    },
  ],
};

const SINGLE_CALCULATIONS: GuidePage = {
  id: 'calculations',
  title: 'Calculations',
  blocks: [
    {
      type: 'p',
      text: 'This section explains how the main values in the Single Actor Graph are calculated. All values are calculated within the current graph, using only the selected actor and the actors directly connected to it.',
    },
    {
      type: 'rows',
      rows: [
        {
          id: 'communities',
          key: 'Communities',
          text: 'Communities are found using the **Louvain method**. The calculation looks for groups where actors have stronger interaction connections with each other than with actors outside the group. Connection direction is ignored. The number of interactions between actors is used as the connection weight. Communities are calculated only from the actors in the current Single Actor Graph.',
        },
        {
          id: 'degree',
          key: 'Degree',
          text: 'Degree counts how many unique actors are connected to an actor. Inbound and outbound connections are counted together. If the same two actors interact many times, they still count as one connection for Degree. Degree counts connections shown in the current graph, not all connections the actor may have in the platform.',
        },
        PAGERANK_ROW,
        {
          id: 'connections',
          key: 'Connections',
          text: 'Connections are counted by unique actors, not by the total number of interactions.',
          why: [
            '**Inbound** counts how many actors have a connection pointing to the selected actor.',
            '**Outbound** counts how many actors the selected actor points to.',
            '**Mutual** counts how many actors have connections with the selected actor in both directions.',
            'Multiple interactions between the same two actors do not create additional connections.',
          ],
        },
        INTERACTION_COUNTS_ROW,
        CRITICAL_BRIDGES_ROW,
        ACTOR_ROLES_ROW,
      ],
    },
  ],
};

// ---------------------------------------------------------------------------
// Understanding the results

const RESULT_ROWS: Record<string, GuideRow> = {
  directOnly: {
    id: 'direct-only',
    key: 'The graph shows only direct connections',
    text: 'The graph includes only actors directly connected to the selected actor. Actors connected through one or more other actors are not shown.',
  },
  directStrength: {
    id: 'direct-strength',
    key: 'A direct connection does not show relationship strength by itself',
    text: 'A direct connection can come from a single interaction. Use interaction count, direction, and mutual connections for additional context.',
  },
  notEveryActor: {
    id: 'not-every-actor',
    key: 'Not every actor is shown',
    text: 'Actors with fewer than two connections are removed from the graph. This check repeats until every actor left has at least two connections. Smaller or less connected actors may therefore be missing. The graph can show up to **2,000 actors**. If more actors qualify, only the most active are kept.',
  },
  period: {
    id: 'period',
    key: 'The selected period changes the graph',
    text: 'The graph only includes interactions from the selected time period. Changing the period can change which actors, connections, and communities appear.',
  },
  moreActivity: {
    id: 'more-activity',
    key: 'More activity does not prove coordination',
    text: 'A large number of interactions or a sudden increase in activity does not by itself mean that actors are coordinating. The same pattern can also appear around a major event or widely shared content.',
  },
  lessActivity: {
    id: 'less-activity',
    key: 'Less activity does not prove there is no coordination',
    text: 'Coordinated activity can be spread over time or across many actors. A graph with little visible activity does not prove that coordination is absent.',
  },
  notTopics: {
    id: 'not-topics',
    key: 'Communities are not topics',
    text: 'Communities are based on who interacts with whom, not on what actors post about. Two separate communities can discuss or spread the same narrative without interacting with each other.',
  },
  notRealGroup: {
    id: 'not-real-group',
    key: 'Communities are not proof of a real-world group',
    text: 'Actors can appear in the same community because of how their connections are distributed in this graph. This does not mean they are coordinated, share the same intent, or belong to the same real-world organization.',
  },
  outsideGroup: {
    id: 'outside-group',
    key: 'Actors outside the Actor Group can appear',
    text: 'The Actor Group is the starting point for the graph, not a complete boundary around it. Actors marked **Not in actor group** can appear because of connections included in the graph. Their presence does not by itself mean they are important, risky, or part of the original group.',
  },
  extractedData: {
    id: 'extracted-data',
    key: 'The graph depends on extracted publication data',
    text: 'Connections are created from Mentions, Reposts, and Links that the platform can identify in publications. A Link counts only when it can be connected to a known actor, page, channel, or piece of content.',
  },
  notComplete: {
    id: 'not-complete',
    key: 'The graph is not a complete picture of an actor',
    text: 'The graph shows only the connections and interactions included in this graph and time period. An actor may have other activity or connections that are not represented here.',
  },
};

const resultsPage = (variant: ActorVariant): GuidePage => ({
  id: 'limitations',
  title: 'Understanding the results',
  blocks: [
    {
      type: 'p',
      text: 'The graph shows patterns in interaction activity, but it does not show the full picture. Keep these limitations in mind when interpreting what you see.',
    },
    {
      type: 'rows',
      rows: isGroup(variant)
        ? [
            RESULT_ROWS.notEveryActor,
            RESULT_ROWS.period,
            RESULT_ROWS.moreActivity,
            RESULT_ROWS.lessActivity,
            RESULT_ROWS.notTopics,
            RESULT_ROWS.notRealGroup,
            RESULT_ROWS.outsideGroup,
            RESULT_ROWS.extractedData,
            RESULT_ROWS.notComplete,
          ]
        : [
            RESULT_ROWS.directOnly,
            RESULT_ROWS.directStrength,
            RESULT_ROWS.period,
            RESULT_ROWS.moreActivity,
            RESULT_ROWS.lessActivity,
            RESULT_ROWS.notTopics,
            RESULT_ROWS.notRealGroup,
            RESULT_ROWS.extractedData,
            RESULT_ROWS.notComplete,
          ],
    },
  ],
});

export const actorGuideGroups = (variant: ActorVariant): GuideGroup[] => [
  { label: '', pages: [isGroup(variant) ? GROUP_WELCOME : SINGLE_WELCOME] },
  {
    label: 'What you can find out',
    pages: isGroup(variant) ? GROUP_USE_CASES : SINGLE_USE_CASES,
  },
  {
    label: 'How the graph works',
    pages: [
      configurationPage(variant),
      filteringPage(variant),
      howToReadPage(variant),
      isGroup(variant) ? GROUP_CALCULATIONS : SINGLE_CALCULATIONS,
      resultsPage(variant),
    ],
  },
];
